import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { safeError } from '@/lib/safeLog';

declare global {
  interface Window {
    zE?: (...args: unknown[]) => void;
  }
}

const ZENDESK_POLL_MS = 150;
const ZENDESK_READY_MAX_MS = 60_000;

function waitForZendeskReady(signal: AbortSignal): Promise<boolean> {
  if (typeof window === 'undefined') return Promise.resolve(false);
  if (typeof window.zE === 'function') return Promise.resolve(true);
  if (signal.aborted) return Promise.resolve(false);

  return new Promise((resolve) => {
    const start = Date.now();
    // DOM timers are numeric IDs; avoid NodeJS.Timeout vs number conflicts in typings.
    const pollRef: { id?: number } = {};

    const finish = (value: boolean) => {
      if (pollRef.id !== undefined) {
        window.clearInterval(pollRef.id);
        pollRef.id = undefined;
      }
      signal.removeEventListener('abort', onAbort);
      resolve(value);
    };

    const onAbort = () => {
      finish(false);
    };
    signal.addEventListener('abort', onAbort, { once: true });

    pollRef.id = window.setInterval(() => {
      if (signal.aborted) {
        finish(false);
        return;
      }
      if (typeof window.zE === 'function') {
        finish(true);
        return;
      }
      if (Date.now() - start > ZENDESK_READY_MAX_MS) {
        finish(false);
      }
    }, ZENDESK_POLL_MS);
  });
}

function zendeskLogout(): void {
  try {
    if (typeof window.zE === 'function') {
      window.zE('messenger', 'logoutUser');
    }
  } catch (e) {
    safeError('Zendesk logoutUser failed:', e);
  }
}

/** Zendesk rejects stale login attempts when a newer `loginUser` superseded them — not actionable. */
function isSupersededZendeskLoginError(err: unknown): boolean {
  let type: string | undefined;
  let reason: string | undefined;
  if (typeof err === 'object' && err !== null) {
    const o = err as { type?: unknown; reason?: unknown };
    type = typeof o.type === 'string' ? o.type : undefined;
    reason = typeof o.reason === 'string' ? o.reason : undefined;
  } else if (typeof err === 'string') {
    try {
      const o = JSON.parse(err) as { type?: string; reason?: string };
      type = o.type;
      reason = o.reason;
    } catch {
      return false;
    }
  }
  return type === 'LoginFailedError' && reason === 'Not latest request';
}

/**
 * Zendesk returns a 409 email conflict when the JWT email claim is already associated
 * with a different (e.g. anonymous) Zendesk identity. Retry without the email claim.
 * Matches both type and reason to avoid triggering on unrelated errors containing "409".
 */
function is409EmailConflict(err: unknown): boolean {
  let type: string | undefined;
  let reason: string | undefined;
  if (typeof err === 'object' && err !== null) {
    const o = err as { type?: unknown; reason?: unknown };
    type = typeof o.type === 'string' ? o.type : undefined;
    reason = typeof o.reason === 'string' ? o.reason : undefined;
  } else if (typeof err === 'string') {
    try {
      const o = JSON.parse(err) as { type?: string; reason?: string };
      type = o.type;
      reason = o.reason;
    } catch {
      return false;
    }
  }
  return type === 'LoginFailedError' && reason === '409: email conflict';
}

/**
 * Registers Zendesk Messaging `loginUser` when an `AuthProvider` session exists,
 * and calls `logoutUser` when the session ends. JWTs are minted by the
 * `zendesk-messenger-jwt` edge function (never sign in the browser).
 */
export function ZendeskMessengerAuthSync() {
  const { session, isAuthenticated } = useAuth();
  const location = useLocation();
  const loginAttemptRef = useRef(0);

  useEffect(() => {
    const isAdmin = location.pathname.startsWith('/admin');
    const ac = new AbortController();

    void waitForZendeskReady(ac.signal).then((ready) => {
      if (!ready || typeof window.zE !== 'function') return;
      window.zE('messenger', isAdmin ? 'hide' : 'show');
    });

    return () => ac.abort();
  }, [location.pathname]);

  useEffect(() => {
    const attempt = ++loginAttemptRef.current;
    let cancelled = false;
    const ac = new AbortController();

    void (async () => {
      if (!isAuthenticated || !session?.user?.id) {
        zendeskLogout();
        return;
      }

      const ready = await waitForZendeskReady(ac.signal);
      if (cancelled || attempt !== loginAttemptRef.current || !ready || typeof window.zE !== 'function') {
        return;
      }

      // Clear any stale anonymous Zendesk session before identifying the user.
      // This prevents 409 email conflicts caused by the widget associating an email
      // with an anonymous identity on unauthenticated pages before login.
      zendeskLogout();

      const attemptLogin = (omitEmail: boolean) => {
        window.zE!(
          'messenger',
          'loginUser',
          function zendeskJwtCallback(callback: (jwt: string) => void) {
            void (async () => {
              try {
                const { data, error } = await supabase.functions.invoke<{ jwt?: string }>(
                  'zendesk-messenger-jwt',
                  { method: 'GET', headers: omitEmail ? { 'x-omit-email': 'true' } : undefined },
                );
                if (cancelled || attempt !== loginAttemptRef.current) {
                  return;
                }
                if (error || !data?.jwt) {
                  safeError('zendesk-messenger-jwt invoke failed:', error ?? 'no jwt');
                  // Never leave the widget waiting on auth — that can block conversations.
                  queueMicrotask(() => zendeskLogout());
                  return;
                }
                callback(data.jwt);
              } catch (e) {
                if (cancelled || attempt !== loginAttemptRef.current) {
                  return;
                }
                safeError('zendesk-messenger-jwt invoke error:', e);
                queueMicrotask(() => zendeskLogout());
              }
            })();
          },
          function zendeskLoginCallback(err: unknown) {
            if (!err) return;
            if (cancelled || attempt !== loginAttemptRef.current) {
              return;
            }
            if (isSupersededZendeskLoginError(err)) {
              return;
            }
            // 409 email conflict: the email claim is already owned by an anonymous Zendesk
            // identity. Log out to clear the conflicting session, then retry once without
            // the email claim — external_id alone is enough to identify the user in Messenger.
            if (!omitEmail && is409EmailConflict(err)) {
              zendeskLogout();
              attemptLogin(true);
              return;
            }
            safeError('Zendesk messenger loginUser failed:', err);
            queueMicrotask(() => zendeskLogout());
          },
        );
      };

      attemptLogin(false);
    })();

    return () => {
      cancelled = true;
      ac.abort();
      zendeskLogout();
    };
  }, [isAuthenticated, session?.user?.id]);

  return null;
}
